
export interface SettingInfo {
  title?: string;
  description: string;
  modes?: string[]; // If omitted, applicable to all
  keywords?: string[];
}

export const SETTING_INFO: Record<string, SettingInfo> = {
  // TABS & SECTIONS
  sphereStudio: {
    title: "Sphere Studio",
    description: "Advanced controls for ball aesthetics, growth logic, and physical properties of the primary simulation entities."
  },
  masterDisplayControls: {
    title: "Master Display",
    description: "Global rendering settings including color themes, camera behavior, and core visual engines."
  },
  geometryWorld: {
    title: "Geometry & World",
    description: "Configure the arena shape, outer boundaries, and dynamic environment morphing logic."
  },
  trailDynamics: {
    title: "Trail Dynamics",
    description: "Interactive particle engines that create persistent visual artifacts and trails behind moving objects."
  },
  impactEffects: {
    title: "Impact Effects",
    description: "Visual and physical feedback systems triggered by collisions between objects and boundaries."
  },
  parallaxEngine: {
    title: "Parallax Engine",
    description: "High-performance background rendering system with multi-layer depth and mathematical pattern generation."
  },
  titleBranding: {
    title: "Title & Branding",
    description: "Overlay controls for textual elements, watermarks, and simulation labeling."
  },
  layerSorting: {
    title: "Layer Sorting",
    description: "Controls the rendering order of all simulation elements to manage visual depth and occlusion."
  },
  audioSpectrumStudio: {
    title: "Visualizer Bar Studio",
    description: "A professional-grade audio visualization system that reacts to frequencies and collision impacts with dynamic bars."
  },
  advancedPhysics: {
    title: "Advanced Physics",
    description: "Fine-tune the mathematical precision of the physics engine, air resistance, and velocity constraints."
  },
  spawner: {
    title: "Spawner Studio",
    description: "Master engine for entity generation, population limits, and algorithmic deployment protocols."
  },
  spaceSpiralStudio: {
    title: "Space Spiral",
    description: "A cosmic simulation engine that arranges physics entities into a rotating spiral formation with complex gravity and mass dynamics."
  },
  drawer: {
    title: "Painter Simulation Studio",
    description: "A professional physics-based drawing engine that transforms the ball path into artistic geometry."
  },
  rhythmic: {
    title: "Rhythmic Mode Studio",
    description: "Advanced synchronization engine that bridges musical data with physics trajectory and visual pulses."
  },
  hudSettings: {
    title: "Main Interface Telemetry",
    description: "Configure real-time data overlays, performance metrics, and simulation status indicators."
  },
  recorderFPS: {
    title: "Recording Framerate",
    description: "Set the target frames per second for video capture. Lower values save CPU, while higher values (60 FPS) result in buttery smooth videos."
  },
  recorderQuality: {
    title: "Format & Quality",
    description: "Select the video container, resolution presets, and bitrate targets for the final render output."
  },
  recorderGallery: {
    title: "Cloud Render Vault",
    description: "Browse and download your high-fidelty cloud renders. Files are stored globally for 30 days."
  },
  // PARALLAX BACKGROUND
  spaceSpiralMassBox: {
    title: "Box Mass",
    description: "The weight of each box, affecting physics interactions.",
    keywords: ["spiral", "mass", "box", "weight"]
  },
  spaceSpiralMassBall: {
    title: "Ball Mass",
    description: "The weight of the ball.",
    keywords: ["spiral", "mass", "ball", "weight"]
  },
  spaceSpiralGravity: {
    title: "Ball Gravity",
    description: "Enable or disable gravity for the ball in the space spiral.",
    keywords: ["spiral", "gravity", "ball"]
  },
  spaceSpiralGravityPower: {
    title: "Gravity Power",
    description: "The strength of gravity acting on the ball.",
    keywords: ["spiral", "gravity", "power", "strength"]
  },
  spaceSpiralSpeed: {
    title: "Spiral Speed",
    description: "Base speed of the spiral simulation.",
    keywords: ["spiral", "speed", "simulation"]
  },
  spaceSpiralBoxCount: {
    title: "Box Count",
    description: "The number of boxes forming the spiral.",
    keywords: ["spiral", "box", "count", "number"]
  },
  spaceSpiralBoxWidth: {
    title: "Box Width",
    description: "The width of each box in the spiral.",
    keywords: ["spiral", "box", "width", "size"]
  },
  spaceSpiralBoxHeight: {
    title: "Box Height",
    description: "The height of each box in the spiral.",
    keywords: ["spiral", "box", "height", "size"]
  },
  spaceSpiralScale: {
    title: "Spiral Scale",
    description: "The overall size of the spiral formation.",
    keywords: ["spiral", "scale", "size", "formation"]
  },
  spaceSpiralBoxSize: {
    title: "Box Size",
    description: "The base size of boxes in the spiral.",
    keywords: ["spiral", "box", "size"]
  },
  spaceSpiralBoxColor: {
    title: "Box Color",
    description: "The color of the boxes in the spiral.",
    keywords: ["spiral", "box", "color"]
  },
  spaceSpiralColorMode: {
    title: "Color Mode",
    description: "How colors are applied to the boxes in the spiral.",
    keywords: ["spiral", "color", "mode"]
  },
  spaceSpiralColorA: {
    title: "Color A",
    description: "The primary color for boxes.",
    keywords: ["spiral", "color"]
  },
  spaceSpiralColorB: {
    title: "Color B",
    description: "The secondary color for boxes.",
    keywords: ["spiral", "color"]
  },
  spaceSpiralGradientSpeed: {
    title: "Gradient Speed",
    description: "How fast the gradient colors shift.",
    keywords: ["spiral", "gradient", "speed", "color"]
  },
  spaceSpiralBoxBorderWidth: {
    title: "Border Width",
    description: "The thickness of the border around each box.",
    keywords: ["spiral", "box", "border", "width", "thickness"]
  },
  spaceSpiralBoxBorderColor: {
    title: "Border Color",
    description: "The color of the box borders.",
    keywords: ["spiral", "box", "border", "color"]
  },
  spaceSpiralRepeats: {
    title: "Spiral Repeats",
    description: "Number of times the spiral loops on itself.",
    keywords: ["spiral", "repeats", "loops"]
  },
  spaceSpiralSoundSpawn: {
    title: "Spawn Sound",
    description: "The sound played when a new ball is spawned.",
    keywords: ["spiral", "sound", "spawn", "audio"]
  },
  spaceSpiralSoundBounce: {
    title: "Bounce Sound",
    description: "The sound played when the ball hits a box.",
    keywords: ["spiral", "sound", "bounce", "audio"]
  },
  spaceSpiralBoxSpacing: {
    title: "Box Spacing",
    description: "The distance between boxes in the spiral.",
    keywords: ["spiral", "box", "spacing", "distance"]
  },
  spaceSpiralMinBoxScale: {
    title: "Min Box Scale",
    description: "The minimum scale factor for boxes at the center.",
    keywords: ["spiral", "box", "scale", "minimum"]
  },
  spaceSpiralMaxBoxScale: {
    title: "Max Box Scale",
    description: "The maximum scale factor for boxes at the edges.",
    keywords: ["spiral", "box", "scale", "maximum"]
  },
  spaceSpiralRotation: {
    title: "Spiral Rotation",
    description: "The rotation of the entire spiral formation.",
    keywords: ["spiral", "rotation", "angle"]
  },
  spaceSpiralBoxRotation: {
    title: "Box Rotation",
    description: "The rotation of individual boxes.",
    keywords: ["spiral", "box", "rotation", "angle"]
  },
  spaceSpiralBallOffsetX: {
    title: "Ball Offset X",
    description: "The starting horizontal position of the ball.",
    keywords: ["spiral", "ball", "offset", "horizontal", "position"]
  },
  spaceSpiralBallOffsetY: {
    title: "Ball Offset Y",
    description: "The starting vertical position of the ball.",
    keywords: ["spiral", "ball", "offset", "vertical", "position"]
  },
  spaceSpiralThrowAngle: {
    title: "Throw Angle",
    description: "The angle of the manual power throw.",
    keywords: ["spiral", "throw", "angle", "manual", "power"]
  },
  spaceSpiralThrowPower: {
    title: "Throw Power",
    description: "The force of the manual power throw.",
    keywords: ["spiral", "throw", "power", "force", "manual"]
  },
  spaceSpiralForwardThrust: {
    title: "Forward Thrust",
    description: "Initial propulsion force applied to the ball.",
    keywords: ["spiral", "forward", "thrust", "force", "propulsion"]
  },
  spaceSpiralThrustAngle: {
    title: "Thrust Angle",
    description: "The direction of the initial thrust.",
    keywords: ["spiral", "thrust", "angle", "direction"]
  },
  spaceSpiralThrustDuration: {
    title: "Thrust Duration (ms)",
    description: "How long the initial thrust lasts.",
    keywords: ["spiral", "thrust", "duration", "time"]
  },
  spaceSpiralRestitution: {
    title: "Ball Bounciness",
    description: "How much energy the ball retains after a bounce.",
    keywords: ["spiral", "restitution", "ball", "bounce", "energy"]
  },
  spaceSpiralBoxRestitution: {
    title: "Box Bounciness",
    description: "How much energy the boxes retain after a bounce.",
    keywords: ["spiral", "restitution", "box", "bounce", "energy"]
  },
  spaceSpiralAirResistance: {
    title: "Ball Air Resistance",
    description: "How much the ball slows down in the air.",
    keywords: ["spiral", "air", "resistance", "ball"]
  },
  spaceSpiralBoxAirResistance: {
    title: "Box Air Resistance",
    description: "How much the boxes slow down in the air.",
    keywords: ["spiral", "air", "resistance", "box"]
  },
  spaceSpiralEnableRounds: {
    title: "Enable Rounds",
    description: "If enabled, the simulation progresses through a sequence of rounds with different settings.",
    keywords: ["spiral", "enable", "rounds", "sequence"]
  },
  spaceSpiralRoundTimeLimit: {
    title: "Round Time Limit",
    description: "The maximum time allowed per round.",
    keywords: ["spiral", "round", "time", "limit"]
  },
  parallaxEnabled: { description: "Enables or disables the parallax background rendering engine." },
  parallaxEffect: { description: "Selects the specific visual algorithm used for the background layers." },
  parallaxImageUrl: { description: "A direct URL to a PNG or JPG image used as the media layer." },
  parallaxVideoUrl: { description: "A direct URL to an MP4 video used as the media layer." },
  parallaxMediaFit: { description: "How the media scales within the background (Cover, Contain, Stretch, Tile)." },
  parallaxMediaOpacity: { description: "Transparency level of the media layer." },
  parallaxIntensity: { description: "The strength of depth effect when moving the camera or objects." },
  parallaxSpeed: { description: "How fast the animated layers flow over time." },
  parallaxLayers: { description: "The number of distinct depth layers rendered. More layers decrease performance." },
  parallaxOpacity: { description: "Global transparency of the parallax background." },
  parallaxScale: { description: "Size multiplier for the textures and shapes in the parallax." },
  parallaxComplexity: { description: "Enhances the intricate details of the mathematical patterns." },
  parallaxRotation: { description: "Rotates the entire parallax canvas." },
  parallaxPatternDensity: { description: "Controls how repeated or dense the patterns appear." },
  parallaxZoomInfluence: { description: "How much the camera zoom affects the parallax depth." },
  parallaxBlur: { description: "Applies a blur effect to the background layers to simulate depth of field." },
  parallaxBrightness: { description: "Adjusts the brightness of the generated background." },
  parallaxContrast: { description: "Enhances or reduces the contrast of the colors." },
  parallaxSaturation: { description: "Controls the vibrancy of the background colors." },
  parallaxAutoScrollX: { description: "Constant drift speed on the X-axis." },
  parallaxAutoScrollY: { description: "Constant drift speed on the Y-axis." },
  parallaxGlowEnabled: { description: "Applies a bloom/glow effect to the bright parts of the background." },
  parallaxGlowRadius: { description: "The spread size of the glow." },
  parallaxGlowOpacity: { description: "The brightness or visibility of the glow." },
  parallaxColorMode: { description: "How colors are mapped (Theme Adaptive, Custom, Rainbow)." },
  parallaxCustomColor: { description: "A specific hex color to tint the background." },

  // RGB ENGINE
  useBallRGB: {
    title: "Ball Pulse",
    description: "Enables individual RGB cycling for every ball sphere.",
    keywords: ["rgb", "ball", "pulse"]
  },
  useFXRGB: {
    title: "VFX Glow",
    description: "Syncs collision impact visuals with the global RGB engine.",
    keywords: ["rgb", "fx", "glow"]
  },
  useTrailRGB: {
    title: "Trail Flux",
    description: "Applies a flowing RGB gradient to active particle trails.",
    keywords: ["rgb", "trail", "flux"]
  },
  useContainerRGB: {
    title: "Edge Aura",
    description: "Lights up the container boundaries with dynamic RGB cycles.",
    keywords: ["rgb", "container", "aura"]
  },

  // VIDEO / RECORDER
  videoExportQuality: {
    title: "Export Quality",
    description: "Adjust the visual fidelity of the rendered video output.",
    keywords: ["video", "quality", "export"]
  },

  // SPAWNER ENGINE
  spawnOnWallCollision: {
    title: "Wall Bounces",
    description: "Generate a new ball whenever one hits a wall.",
    modes: ["SPAWNER"]
  },
  spawnOnBallCollision: {
    title: "Ball Hits",
    description: "Generate a new ball whenever two balls collide.",
    modes: ["SPAWNER"]
  },
  initialBalls: {
    title: "Initial Ball Count",
    description: "The number of balls to start with when the simulation begins.",
    modes: ["SPAWNER"]
  },
  spawnerEngineMode: {
    title: "Spawn Style",
    description: "Defines the spawning logic (Classic, Split, Stream, etc).",
    modes: ["SPAWNER"]
  },
  spawnerPattern: {
    title: "Shape",
    description: "Select the geometry for Shape mode templates.",
    modes: ["SPAWNER"]
  },
  spawnerBurstCount: {
    title: "Spawn Amount",
    description: "The number of balls generated in each batch.",
    modes: ["SPAWNER"]
  },
  spawnerSpawnInterval: {
    title: "Spawn Gap",
    description: "Wait time between each new ball created in Stream mode.",
    modes: ["SPAWNER"]
  },

  // RHYTHMIC ENGINE extras
  rhythmicPulseActive: {
    title: "Pulse Animation",
    description: "Makes objects physically pulse in size with the audio beat.",
    modes: ["RHYTHMIC"]
  },
  rhythmicGlowActive: {
    title: "Glow Animation",
    description: "Syncs visual bloom and glow intensity with audio peaks.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSensitivity: {
    title: "Audio Sensitivity",
    description: "Controls how much the audio volume influences visual pulses.",
    modes: ["RHYTHMIC"]
  },
  rhythmicBreakThreshold: {
    title: "Break Threshold",
    description: "Volume level required to trigger a rhythmic break or reset.",
    modes: ["RHYTHMIC"]
  },

  // GENERAL DISPLAY
  darkMode: {
    description: "Switch between light and dark user interface themes for better visibility in different environments.",
  },
  mode: {
    description: "The primary logic of the simulation. SPAWNER: Balls create more balls on contact. PAINTER: Draw lines with physics. ESCAPE: Break rings to exit. RHYTHMIC: Music-driven bounce sync. SPIRAL: A cosmic rotating box.",
  },
  enableTrails: {
    description: "Injects the Trail Engine into the current mode, creating persistent visual and physical paths.",
    modes: ["GLOBAL"]
  },
  enableRhythmic: {
    description: "Injects the Rhythmic Sync engine, making all physics and visuals respond to audio beats.",
    modes: ["GLOBAL"]
  },
  enableEscape: {
    description: "Injects the Escape Ring system into the current mode, adding concentric breakable boundaries.",
    modes: ["GLOBAL"]
  },
  showHUD: {
    description: "Toggle the Heads-Up Display which shows real-time simulation data like FPS, speed, and ball count.",
  },
  rgbLighting: {
    description: "Enable dynamic RGB color cycling for various elements in the simulation.",
  },
  cameraFollow: {
    title: "Enabled",
    description: "Automatically pan and zoom the camera to keep the primary ball centered.",
  },
  cameraSmoothness: {
    title: "Smoothness",
    description: "Controls how fluidly the camera tracks the ball. Higher values make the camera stickier, while lower values create a more cinematic lag.",
  },
  cameraLookahead: {
    title: "Prediction",
    description: "Adjusts how much the camera anticipates the ball's future position based on its current velocity.",
  },
  cameraDeadzone: {
    title: "Deadzone",
    description: "The area around the center where the ball can move without triggering camera movement.",
  },
  cameraZoom: {
    title: "Zoom",
    description: "The base magnification level of the simulation view.",
  },
  cameraZoomSmoothness: {
    title: "Zoom Speed",
    description: "How smoothly the camera transitions between different zoom levels.",
  },
  cameraPredictiveLookahead: {
    title: "Smart Pathing",
    description: "Uses advanced physics prediction to keep the camera ahead of the ball's trajectory.",
  },
  cameraDamping: {
    title: "Motion Damping",
    description: "Adds weight to the camera movement, preventing sudden jerks and jitter.",
  },
  cameraElasticity: {
    title: "Elasticity",
    description: "Determines how 'springy' the camera feels when catching up to the ball.",
  },
  
  // BALL SETTINGS
  ballRadius: {
    description: "The size of the balls in pixels. Larger balls have more surface area for collisions.",
    modes: ["GLOBAL"]
  },
  ballMass: {
    description: "Determines how much the ball influences other objects during collisions. Heavier balls are harder to move.",
    modes: ["GLOBAL"]
  },
  bounceAngleRandomization: {
    title: "Bounce Randomization",
    description: "Adds a random offset to the ball's reflection angle when it hits a boundary. 0% is a perfect reflection. This setting is automatically bypassed in Rhythmic mode when MIDI data is actively controlling the trajectory.",
    modes: ["GLOBAL"]
  },
  initialSpeed: {
    description: "The starting velocity multiplier for all balls.",
    modes: ["GLOBAL"]
  },
  ballColor: {
    description: "The base color of the balls if RGB lighting is disabled.",
    modes: ["GLOBAL"]
  },
  ballGrowthMode: {
    description: "Determines how balls grow: over time, on collision, or synchronized with the total playlist duration.",
    modes: ["GLOBAL"]
  },
  ballGrowthRate: {
    description: "The speed at which balls grow when Growth Mode is active.",
    modes: ["GLOBAL"]
  },
  ballMaxRadius: {
    description: "The maximum size a ball can reach through growth.",
    modes: ["GLOBAL"]
  },
  ballRotationMode: {
    description: "How the ball's visual rotation is determined (e.g., matching its velocity or spinning at a constant rate).",
    modes: ["GLOBAL"]
  },
  ballWeightGrowthEnabled: {
    description: "Gradually increase the ball's mass over time, making it heavier and more impactful.",
  },
  ballMinWeight: {
    description: "The starting mass of the ball when Weight Growth is enabled.",
  },
  ballMaxWeight: {
    description: "The maximum mass a ball can reach through growth.",
  },
  ballWeightGrowthTime: {
    description: "The time in seconds it takes for the ball to reach its maximum weight.",
  },
  ballSquashStretch: {
    description: "A visual effect that deforms the ball based on its velocity and collisions, making it look more dynamic.",
  },
  ballSquashFactor: {
    description: "The intensity of the squash and stretch effect.",
  },
  ballPulseSpeed: {
    description: "The speed at which the ball's size pulses over time.",
  },
  ballFillType: {
    description: "The method used to fill the ball's interior: solid color, radial gradient, linear gradient, or hollow.",
  },
  ballFillOpacity: {
    description: "The transparency of the ball's interior fill.",
  },
  ballGradientInner: {
    description: "The starting color for gradient fills.",
  },
  ballGradientOuter: {
    description: "The ending color for gradient fills.",
  },
  ballTexture: {
    description: "An optional pattern overlay applied to the ball's surface.",
  },
  ballTextureOpacity: {
    description: "The transparency of the texture overlay.",
  },
  ballStrokeWidth: {
    description: "The thickness of the ball's outline.",
  },
  ballStrokeOpacity: {
    description: "The transparency of the ball's outline.",
  },
  ballStrokeColor: {
    description: "The color of the ball's outline.",
  },
  ballGlowRadius: {
    description: "The size of the glow effect around the ball.",
  },
  parallaxWatermarkText: {
    title: "Watermark Text",
    description: "Custom text displayed in a repeating pattern behind the simulation.",
  },
  parallaxWatermarkOpacity: {
    description: "How transparent the watermark text should be.",
  },
  parallaxWatermarkFontSize: {
    description: "The size of the watermark text.",
  },
  parallaxWatermarkRepeatX: {
    description: "How many times the watermark repeats horizontally.",
  },
  parallaxWatermarkRepeatY: {
    description: "How many times the watermark repeats vertically.",
  },
  parallaxWatermarkAngle: {
    description: "The rotation angle of the watermark grid.",
  },
  parallaxWatermarkSpeed: {
    description: "The speed at which the watermark scrolls in the background.",
  },
  ballGlowOpacity: {
    description: "The intensity of the glow effect.",
  },
  ballGlowColor: {
    description: "The color of the glow effect.",
  },
  ballShape: {
    description: "The geometric shape of the balls. This affects their visual appearance and collision behavior.",
  },
  
  // TRAIL SETTINGS
  trailPersistence: {
    description: "When enabled, trail segments never disappear, creating a permanent drawing of the ball's path.",
    modes: ["TRAILS"]
  },
  trailLength: {
    description: "The number of segments in the ball's trail. Higher values create longer trails.",
    modes: ["TRAILS"]
  },
  trailInterval: {
    description: "How often (in milliseconds) a new trail segment is generated.",
    modes: ["TRAILS"]
  },
  trailOpacity: {
    description: "The transparency of the trail segments.",
    modes: ["TRAILS"]
  },
  trailWidthMultiplier: {
    description: "The thickness of the trail segments relative to the ball size.",
    modes: ["TRAILS"]
  },
  trailGlowAmount: {
    description: "The intensity of the glow effect around the trail segments.",
    modes: ["TRAILS"]
  },
  trailOcclusionOptimization: {
    description: "Deletes trail segments that are completely hidden behind newer, larger segments. Recommended for long-running persistent trails.",
    modes: ["TRAILS"]
  },
  trailDistanceBased: {
    description: "When enabled, trail segments are spawned based on the distance the ball has traveled rather than time. This ensures consistent trail density regardless of ball speed.",
    modes: ["TRAILS"]
  },
  trailDistanceInterval: {
    description: "The distance (in pixels) the ball must travel before a new trail segment is spawned when Distance Based Spawning is enabled.",
    modes: ["TRAILS"]
  },
  trailStrokeWidth: {
    description: "The thickness of the outline around each trail segment.",
    modes: ["TRAILS"]
  },
  trailStrokeColor: {
    description: "The color of the outline around each trail segment.",
    modes: ["TRAILS"]
  },
  trailApplyBorderToPersistence: {
    description: "Whether to show the outline on trails even when Infinite Persistence is enabled.",
    modes: ["TRAILS"]
  },
  trailCustomColor: {
    description: "The color of the trail when Rainbow Fill is disabled.",
    modes: ["TRAILS"]
  },
  trailStrokeOpacity: {
    description: "The transparency of the trail's outline.",
    modes: ["TRAILS"]
  },
  trailStartScale: {
    description: "The size of a trail segment when it is first created.",
    modes: ["TRAILS"]
  },
  trailEndScale: {
    description: "The size of a trail segment just before it fades out.",
    modes: ["TRAILS"]
  },
  trailMatchBallSize: {
    description: "When enabled, trail segments will exactly match the ball's size at the moment they are created, ignoring the Start and End Scale settings. This is ideal for persistent trails that should remain static even if the ball grows later.",
    modes: ["TRAILS"]
  },
  trailEnableBorderBreak: {
    description: "Automatically restart the simulation when the trail reaches the container boundary.",
    modes: ["TRAILS"]
  },
  trailStartPosX: {
    description: "The starting horizontal position of the ball in Trail mode.",
    modes: ["TRAILS"]
  },
  trailStartPosY: {
    description: "The starting vertical position of the ball in Trail mode.",
    modes: ["TRAILS"]
  },
  trailStartAngle: {
    description: "The starting movement angle of the ball in Trail mode.",
    modes: ["TRAILS"]
  },
  trailStartSpeed: {
    description: "The starting speed of the ball in Trail mode.",
    modes: ["TRAILS"]
  },

  // PHYSICS & DYNAMICS
  gravity: {
    description: "The downward force acting on all balls. Higher values pull balls down faster.",
    modes: ["GLOBAL"]
  },
  restitution: {
    description: "The 'bounciness' of collisions. 1.0 is perfectly elastic, while lower values lose energy on impact.",
    modes: ["GLOBAL"]
  },
  physicsSubsteps: {
    title: "Physics Substeps",
    description: "The number of physics calculations per frame. Higher values improve accuracy but increase CPU load.",
    modes: ["GLOBAL"]
  },
  airResistance: {
    title: "Air Resistance",
    description: "A force that slows down balls over time. 0 is no resistance, higher values slow balls faster.",
    modes: ["GLOBAL"]
  },
  maxVelocityLimit: {
    title: "Max Velocity",
    description: "The maximum speed a ball can reach. Prevents balls from flying out of bounds at extreme speeds.",
    modes: ["GLOBAL"]
  },
  minVelocityLimit: {
    title: "Min Velocity",
    description: "The minimum speed a ball will maintain. Prevents balls from coming to a complete stop.",
    modes: ["GLOBAL"]
  },
  antiStallMultiplier: {
    title: "Anti-Stall Force",
    description: "A small force applied to balls that are moving too slowly to keep the simulation dynamic.",
    modes: ["GLOBAL"]
  },
  speedIncreaseMode: {
    description: "Determines if and how the simulation speed increases over time or on collisions.",
    modes: ["GLOBAL"]
  },
  speedIncreaseRate: {
    description: "The rate at which simulation speed increases.",
    modes: ["GLOBAL"]
  },
  speedAcceleration: {
    description: "How the speed increase rate itself changes over time.",
    modes: ["GLOBAL"]
  },
  masterSpeed: {
    description: "A global multiplier for all movement speeds in the simulation.",
    modes: ["GLOBAL"]
  },
  ballGrowthAcceleration: {
    description: "How the growth rate changes over time.",
    modes: ["GLOBAL"]
  },
  containerShrinkMode: {
    description: "Determines if the outer container should shrink over time or on collisions.",
    modes: ["GLOBAL"]
  },
  containerShrinkRate: {
    description: "How fast the container shrinks.",
    modes: ["GLOBAL"]
  },
  containerShrinkAcceleration: {
    description: "How the shrink rate changes over time.",
    modes: ["GLOBAL"]
  },
  minContainerRadius: {
    description: "The minimum size the container can reach.",
    modes: ["GLOBAL"]
  },

  // RHYTHMIC SYNC
  rhythmicTimeScale: {
    description: "The base speed of the rhythmic pulse and ball movement.",
    modes: ["RHYTHMIC", "RHYTHMIC_ESCAPE"]
  },
  rhythmicTimeScaleIncreaseMode: {
    description: "How the playback speed changes over time or based on events.",
    modes: ["RHYTHMIC", "RHYTHMIC_ESCAPE"]
  },
  rhythmicTimeScaleRate: {
    description: "How fast the playback speed increases.",
    modes: ["RHYTHMIC", "RHYTHMIC_ESCAPE"]
  },
  rhythmicTimeScaleAcceleration: {
    description: "Secondary acceleration for the time scale.",
    modes: ["RHYTHMIC", "RHYTHMIC_ESCAPE"]
  },
  rhythmicCircleScale: {
    description: "A multiplier for the size of the rhythmic rings.",
    modes: ["RHYTHMIC", "RHYTHMIC_ESCAPE"]
  },
  rhythmicSyncMode: {
    title: "Sync Mode",
    description: "How the ball's movement synchronizes with the rhythmic pulses.",
    modes: ["RHYTHMIC", "RHYTHMIC_ESCAPE"]
  },
  physicsDynamics: {
    title: "Physics & Dynamics",
    description: "Fundamental laws governing the simulation, including gravity, collision bounciness, and object mass interactions.",
  },
  dynamicsLab: {
    title: "Dynamics Lab",
    description: "Experimental controls for world speed acceleration and precision physics processing.",
  },
  expansionDynamics: {
    title: "Expansion Dynamics",
    description: "Configure how physical entities grow or contract based on time, collisions, or audio events.",
  },
  rhythmicSpectrumEnabled: {
    title: "Enable Spectrum",
    description: "Visualizes audio frequencies and collision impacts as a dynamic spectrum around the container boundary.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumBarCount: {
    title: "Bar Count",
    description: "The number of bars in the audio spectrum visualization.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumMaxHeight: {
    title: "Max Height",
    description: "The maximum height of the spectrum bars at peak intensity.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumMinHeight: {
    title: "Min Height",
    description: "The minimum height of the spectrum bars when no audio or collision is detected.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumBarWidth: {
    title: "Bar Width",
    description: "The thickness of each individual spectrum bar.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumBarSpacing: {
    title: "Bar Spacing",
    description: "The gap between adjacent spectrum bars.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumDuration: {
    title: "Decay Time",
    description: "How long the spectrum bars remain visible after a collision event.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumColorMode: {
    title: "Color Mode",
    description: "Determines the color scheme of the spectrum: matching the ball, rainbow, custom, or dynamic pulses.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumCustomColor: {
    title: "Custom Bar Color",
    description: "Select a specific color for the spectrum bars when using 'CUSTOM' color mode.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumGrowRate: {
    title: "Grow Speed",
    description: "How quickly the bars respond to new audio or collision inputs.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumShrinkRate: {
    title: "Shrink Speed",
    description: "How quickly the bars decay back to their minimum height.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumAudioSensitivity: {
    title: "Audio Sensitivity",
    description: "Multiplies the intensity of the audio-driven spectrum response.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumCollisionIntensity: {
    title: "Collision Impact",
    description: "Multiplies the intensity of the collision-driven spectrum response.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumCollisionSpread: {
    title: "Impact Spread",
    description: "How many neighboring bars are affected by a single collision point.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumAudioSpatiality: {
    title: "Audio Spatiality",
    description: "Controls how much the audio affects bars further from the collision point. 0 is global, 1 is fully spatialized.",
    modes: ["RHYTHMIC"]
  },
  rhythmicSpectrumGlobalAudio: {
    title: "Global Audio",
    description: "When enabled, audio frequencies are shown on all bars simultaneously. When disabled, audio only affects bars near recent collision points.",
    modes: ["RHYTHMIC"]
  },
  playlistEnabled: {
    description: "Enable sequential playback of multiple songs in a playlist.",
    modes: ["RHYTHMIC"]
  },
  playlistLoop: {
    description: "Automatically restart the playlist from the beginning after the last song finishes.",
    modes: ["RHYTHMIC"]
  },
  playlistCurrentIndex: {
    description: "The index of the currently playing song in the playlist.",
    modes: ["RHYTHMIC"]
  },
  playlist: {
    description: "The list of songs and their associated presets to be played sequentially.",
    modes: ["RHYTHMIC"]
  },

  // HUD SETTINGS
  hudPosition: {
    description: "Controls where the HUD is anchored on the screen.",
  },
  hudOpacity: {
    description: "The transparency level of the HUD background and text.",
  },
  hudScale: {
    description: "Adjusts the overall size of the HUD elements.",
  },
  hudShowElapsedTime: {
    description: "Shows how long the current simulation has been running.",
  },
  hudShowSpeed: {
    description: "Displays the current speed of the balls.",
  },
  hudShowBallCount: {
    description: "Displays the total number of balls currently in the simulation.",
  },
  hudShowBounceCount: {
    description: "Displays the total number of wall collisions recorded.",
  },
  hudShowRingsLeft: {
    description: "In Escape mode, shows how many rings are remaining.",
    modes: ["ESCAPE"]
  },
  hudShowTimeRemaining: {
    description: "In Escape mode, shows the time left to clear the rings.",
    modes: ["ESCAPE"]
  },
  hudShowFPS: {
    description: "Displays the current frames per second (performance metric).",
  },
  hudShowCurrentRound: {
    description: "In Space Spiral mode, shows the current level or round.",
    modes: ["SPACE_SPIRAL"]
  },
  hudShowContainerRadius: {
    description: "Displays the current size of the container.",
  },
  hudShowBallRadius: {
    description: "Displays the current size of the balls.",
  },
  hudShowGravity: {
    description: "Displays the current gravity strength.",
  },
  hudShowRestitution: {
    title: "Show Restitution",
    description: "Displays the current bounciness factor.",
  },
  hudShowBallMass: {
    title: "Show Ball Mass",
    description: "Displays the current weight of the balls in the HUD.",
  },
  hudShowTimeScale: {
    title: "Show Time Scale",
    description: "Displays the current world time-scale multiplier.",
  },
  hudShowInitialSpeed: {
    title: "Show Initial Speed",
    description: "Displays the starting speed configured for this session.",
  },
  hudShowSpeedIncreaseRate: {
    title: "Show Speed Increase Rate",
    description: "Displays how fast the simulation speed accelerates.",
  },
  hudShowAirResistance: {
    title: "Show Air Resistance",
    description: "Displays the current air friction coefficient.",
  },
  hudShowPhysicsSubsteps: {
    title: "Show Physics Substeps",
    description: "Displays the current precision level of the physics engine.",
  },
  hudShowBallGrowthRate: {
    title: "Show Ball Growth Rate",
    description: "Displays how quickly the spheres are expanding.",
  },
  hudShowContainerShrinkRate: {
    title: "Show Container Shrink Rate",
    description: "Displays how quickly the arena is contracting.",
  },
  showBallSizeCounter: {
    title: "Ball Size Counter",
    description: "Displays a dedicated floating text label showing the current size of each ball.",
  },
  ballSizeCounterPosition: {
    title: "Counter Position",
    description: "Where the ball size label is anchored relative to the screen or the ball.",
  },
  ballSizeCounterOffset: {
    title: "Counter Offset",
    description: "A vertical pixel offset for the ball size text.",
  },
  ballSizeCounterPrefix: {
    title: "Counter Prefix",
    description: "Text displayed before the ball size number.",
  },
  ballSizeCounterSuffix: {
    title: "Counter Suffix",
    description: "Text displayed after the ball size number.",
  },
  ballSizeCounterColor: {
    title: "Counter Color",
    description: "The color of the ball size text.",
  },
  ballSizeCounterUseRGB: {
    title: "Use RGB",
    description: "If enabled, the ball size text cycles through rainbow colors.",
  },
  ballSizeCounterFontSize: {
    title: "Font Size",
    description: "The size of the ball size text.",
  },
  ballSizeCounterDecimalPlaces: {
    title: "Decimals",
    description: "How many decimal points to show for the ball size.",
  },
  ballSizeCounterOpacity: {
    title: "Opacity",
    description: "Transparency of the ball size text.",
  },
  ballSizeCounterShadowColor: {
    title: "Shadow Color",
    description: "The color of the drop shadow behind the ball size text.",
  },
  ballSizeCounterShadowBlur: {
    title: "Shadow Blur",
    description: "The blurriness of the drop shadow.",
  },
  showBounceCounter: {
    title: "Show Bounce Counter",
    description: "Toggles a large floating counter that tracks successful wall impacts.",
  },
  bounceCounterPosition: {
    title: "Counter Position",
    description: "Where the bounce counter is anchored on the screen.",
  },
  bounceCounterOffset: {
    title: "Counter Offset",
    description: "A vertical pixel offset for the bounce counter text.",
  },
  bounceCounterPrefix: {
    title: "Counter Prefix",
    description: "Text displayed before the bounce count.",
  },
  bounceCounterSuffix: {
    title: "Counter Suffix",
    description: "Text displayed after the bounce count.",
  },
  bounceCounterColor: {
    title: "Counter Color",
    description: "The color of the bounce counter text.",
  },
  bounceCounterUseRGB: {
    title: "Use RGB",
    description: "If enabled, the bounce counter text cycles through rainbow colors.",
  },
  bounceCounterFontSize: {
    title: "Font Size",
    description: "The size of the bounce counter text.",
  },
  bounceCounterOpacity: {
    title: "Opacity",
    description: "Transparency of the bounce counter text.",
  },
  bounceCounterShadowColor: {
    title: "Shadow Color",
    description: "The color of the drop shadow behind the bounce counter text.",
  },
  bounceCounterShadowBlur: {
    title: "Shadow Blur",
    description: "The blurriness of the drop shadow.",
  },
  showControlIsland: {
    description: "Toggles the floating play and restart buttons at the bottom of the screen.",
  },
  hudShowMode: {
    description: "Display the name of the currently active simulation mode.",
  },

  // BALL PLACEMENT
  ballPlacementMode: {
    description: "How balls are positioned at the start: center, manual coordinates, along the border, or at a specific angle.",
  },
  ballPlacementPosX: {
    description: "The horizontal position of the balls (as a percentage of the container width).",
  },
  ballPlacementPosY: {
    description: "The vertical position of the balls (as a percentage of the container height).",
  },
  ballPlacementBorder: {
    description: "Which side of the container balls should spawn on.",
  },
  ballPlacementAngle: {
    description: "The angle at which balls are placed relative to the center.",
  },
  ballPlacementDistance: {
    description: "The distance from the center at which balls are placed.",
  },
  ballPlacementDirectionAngle: {
    description: "The initial direction in which balls move.",
  },
  ballPlacementRandomDirection: {
    description: "If enabled, balls will move in a random direction at the start.",
  },
  ballPlacementIndividualEnabled: {
    description: "Allows you to configure the starting position and velocity for each ball independently.",
  },

  // GLOBAL VISUALS
  hueCycleSpeed: {
    description: "The speed at which colors shift over time for rainbow effects.",
  },
  // NEW KEYS FOR STUDIO UI
  drawerCoreLogic: {
    title: "Core Logic",
    description: "Primary behavior settings for the painter engine, including reversing drawing and path optimization."
  },
  drawerGeometrySymmetry: {
    title: "Geometry & Symmetry",
    description: "Mirrors and replicates your drawing across multiple axes for complex geometric patterns."
  },
  drawerBrushStyle: {
    title: "Brush Style",
    description: "Select the visual texture and shape of the painter strokes (Solid, Neon, Sketchy, etc)."
  },
  drawerColorDynamics: {
    title: "Color Dynamics",
    description: "Configure how colors evolve along the stroke path, including gradients and RGB cycling."
  },
  drawerPhysicsConstraints: {
    title: "Physics & Constraints",
    description: "Adjust how the painter responds to ball speed, acceleration, and world boundaries."
  },
  drawerPathAttraction: {
    title: "Path Attraction",
    description: "Controls whether strokes are attracted to or repelled by the nearest ball physics point."
  },
  physicsAdvancedTuning: {
    title: "Integrator Tuning",
    description: "Manually adjust the physics integrator's substeps, drag, and velocity damping for extreme simulations."
  },
  spaceSpiralPhysics: {
    title: "Spiral Physics",
    description: "The core locomotive forces acting within the space spiral, including gravity and forward thrust."
  },
  spaceSpiralGeometry: {
    title: "Geometry Engine",
    description: "Algorithmic controls for the size, count, and scale distribution of boxes in the spiral."
  },
  spaceSpiralAesthetics: {
    title: "Aesthetic Profile",
    description: "Personalize the visual identity of the spiral with custom color fluxes and border styling."
  },
  spaceSpiralKinematics: {
    title: "Kinematics",
    description: "Mass and collision properties for both simulation balls and spiral boxes."
  },
  spaceSpiralSounds: {
    title: "Sonic Pulse",
    description: "Audio triggers for collision events within the cosmic spiral environment."
  },
  spaceSpiralSequence: {
    title: "Round Sequence Planner",
    description: "Create complex multi-stage protocols where physics variables change over time across rounds."
  },
  rhythmicStudio: {
    title: "Rhythmic Studio",
    description: "The master control center for synchronizing physics with audio and melody data."
  },
  shakeIntensityMultiplier: {
    description: "Multiplies the intensity of screen shake effects triggered by collisions.",
  },
  shakeDecayRate: {
    description: "How quickly the screen shake effect fades away after an impact.",
  },
  popDecayRate: {
    description: "How quickly the bounce counter 'pop' animation shrinks back to normal size.",
  },

  // LAYER SORTING
  layer_WALLS: {
    description: "The outer container boundary. Usually rendered first as the background structure.",
  },
  layer_RINGS: {
    description: "Concentric rings used in Escape mode. They sit above the background but below the balls.",
  },
  layer_TRAIL: {
    description: "Visual paths left by moving balls. Can be rendered behind or in front of balls for different effects.",
  },
  layer_BALL: {
    description: "The primary physics objects. Usually rendered near the top for maximum visibility.",
  },
  layer_FX: {
    description: "Visual ripples and impact effects triggered by collisions.",
  },
  layer_BLAST: {
    description: "Shattered pieces and explosion effects when balls break.",
  },
  layer_HUD: {
    description: "User interface elements like timers and counters. Usually rendered on top of everything.",
  },

  // IMPACT FX
  collisionEffect: {
    description: "The visual animation that plays when a ball hits the container wall.",
    modes: ["GLOBAL"]
  },
  collisionEffectDuration: {
    description: "How long the impact animation lasts in milliseconds.",
    modes: ["GLOBAL"]
  },
  collisionEffectParticleCount: {
    description: "The number of particles or elements spawned by the impact effect.",
    modes: ["GLOBAL"]
  },
  collisionEffectScale: {
    description: "A multiplier for the size of the impact effect.",
    modes: ["GLOBAL"]
  },
  collisionEffectGravity: {
    description: "The strength of gravity applied to impact particles.",
    modes: ["GLOBAL"]
  },
  collisionEffectColorMode: {
    description: "How the impact effect is colored: matching the ball, rainbow, or custom colors.",
    modes: ["GLOBAL"]
  },
  collisionEffectColor1: {
    description: "The primary color for custom-colored impact effects.",
    modes: ["GLOBAL"]
  },
  collisionEffectColor2: {
    description: "The secondary color for gradient-based impact effects.",
    modes: ["GLOBAL"]
  },

  // SHAPE SETTINGS
  containerShape: {
    description: "The geometric shape of the outer container. Balls bounce off its inner walls.",
    modes: ["GLOBAL"]
  },
  initialContainerShape: {
    description: "The shape the container resets to when the simulation is restarted.",
    modes: ["GLOBAL"]
  },
  containerRotation: {
    description: "The rotation angle of the outer container in degrees.",
    modes: ["GLOBAL"]
  },
  shapeMorphEnabled: {
    description: "Automatically cycle through different container shapes over time.",
    modes: ["GLOBAL"]
  },
  shapeMorphContinuous: {
    description: "If enabled, the shape will constantly transition. If disabled, it changes at fixed intervals.",
    modes: ["GLOBAL"]
  },
  shapeMorphSpeed: {
    description: "The time in seconds between shape changes when Continuous Shift is disabled.",
    modes: ["GLOBAL"]
  },
  shapeMorphDuration: {
    title: "Morph Duration",
    description: "The duration of the transition animation between two shapes.",
    modes: ["GLOBAL"]
  },
  morphableShapes: {
    title: "Morph Cycle Pool",
    description: "Select which shapes are included in the automatic morphing cycle. At least one shape must be selected.",
    modes: ["GLOBAL"]
  },

  // ESCAPE MODE
  escapeRingCount: {
    description: "The number of concentric rings the ball must break through to escape.",
    modes: ["ESCAPE", "RHYTHMIC_ESCAPE"]
  },
  escapeGapSize: {
    description: "The size of the opening in each ring.",
    modes: ["ESCAPE", "RHYTHMIC_ESCAPE"]
  },
  escapeRotationSpeed: {
    description: "How fast the rings rotate, making the gaps harder to hit.",
    modes: ["ESCAPE", "RHYTHMIC_ESCAPE"]
  },
  escapeGapAlignment: {
    description: "How the gaps in the rings are positioned relative to each other.",
    modes: ["ESCAPE", "RHYTHMIC_ESCAPE"]
  },
  escapeRingMinRadius: {
    description: "The radius of the innermost ring.",
    modes: ["ESCAPE"]
  },
  escapeRingSpacing: {
    description: "The distance between consecutive rings.",
    modes: ["ESCAPE"]
  },
  escapeRingThickness: {
    description: "The visual thickness of the escape rings.",
    modes: ["ESCAPE"]
  },

  spawnerVelocityInheritance: {
    title: "Inherit Speed",
    description: "How much speed the new ball takes from the ball that created it.",
    modes: ["SPAWNER"]
  },
  spawnerVelocitySpread: {
    title: "Spread Angle",
    description: "The directional randomness of newly spawned balls.",
    modes: ["SPAWNER"]
  },
  fissionEnabled: {
    title: "Split Settings",
    description: "Enable 'Fission' mode where balls split into smaller fragments upon contact.",
    modes: ["SPAWNER"]
  },
  fissionMaxDepth: {
    title: "Split Limit",
    description: "Max number of times a single lineage can split before stopping.",
    modes: ["SPAWNER"]
  },
  fissionSplitCount: {
    title: "Pieces",
    description: "How many smaller balls are produced during each split event.",
    modes: ["SPAWNER"]
  },
  fissionSizeMultiplier: {
    title: "Shrink Amount",
    description: "The size ratio of child balls relative to their parents.",
    modes: ["SPAWNER"]
  },
  fissionVelocityBoost: {
    title: "Split Speed",
    description: "A speed multiplier applied to balls when they break apart.",
    modes: ["SPAWNER"]
  },
  fusionEnabled: {
    title: "Merge Settings",
    description: "Enable 'Fusion' mode where balls combine into a larger entity upon collision.",
    modes: ["SPAWNER"]
  },
  fusionMaxRadius: {
    title: "Max Size",
    description: "The absolute growth limit for merged balls.",
    modes: ["SPAWNER"]
  },
  fusionMassConservation: {
    title: "Slow Giants",
    description: "If active, momentum is conserved, making larger combined balls move slower.",
    modes: ["SPAWNER"]
  },
  ballLifespanEnabled: {
    title: "Auto-Remove",
    description: "Automatically remove balls after a specific lifespan to maintain performance.",
    modes: ["SPAWNER"]
  },
  ballLifespan: {
    title: "Lifespan",
    description: "Seconds an entity remains active before fading out.",
    modes: ["SPAWNER"]
  },
  ballFadeOutPower: {
    title: "Fade Speed",
    description: "Controls the visual curve of the fading effect.",
    modes: ["SPAWNER"]
  },
  ballShrinkOnDeath: {
    title: "Shrink on Death",
    description: "Causes balls to physically shrink as they approach the end of their lifespan.",
    modes: ["SPAWNER"]
  },
  spawnOnAudioPeak: {
    title: "Beat Spawn",
    description: "Trigger new spawns automatically whenever a loud sound or beat is detected.",
    modes: ["SPAWNER"]
  },
  spawnAudioThreshold: {
    title: "Sensitivity",
    description: "The volume level required to trigger an audio-based spawn.",
    modes: ["SPAWNER"]
  },

  // VISUALS & COLORS
  containerLineWidth: {
    title: "Border Thickness",
    description: "The thickness of the arena borders.",
  },
  worldAndBoundary: {
    title: "World & Boundary",
    description: "Configure the physical limits and appearance of the simulation arena, including line thickness and ambient colors.",
  },
  rgbEngine: {
    title: "RGB Engine",
    description: "Master controls for dynamic color synchronization across all simulation elements.",
  },
  ringPattern: {
    title: "Ring Pattern Studio",
    description: "Advanced styling for escape rings, allowing for gradients, rainbows, and individual layer customization.",
  },
  blastFXColor: {
    title: "Blast & FX Colors",
    description: "Customize the colors of collision ripples, internal arena backgrounds, and shatter fragments.",
  },
  visualPresets: {
    title: "Visual Presets",
    description: "Instantly apply curated visual configurations that change colors, styles, and effects across the entire app.",
  },
  borderColor: {
    description: "The color of the arena borders.",
  },
  outerColor: {
    description: "The color of the area outside the arena.",
  },
  innerColor: {
    description: "The background color inside the arena.",
  },
  collisionWaveColor: {
    description: "The color of the waves generated on collision.",
  },
  blastColor: {
    description: "The color of fragments in a victory blast.",
  },
  blastMatchRingColor: {
    description: "If enabled, blast shards will match the color of the ring that was broken.",
  },

  // AUDIO WORKSHOP
  selectedMelody: {
    description: "The current song or melody being played in Rhythmic mode.",
    modes: ["RHYTHMIC"]
  },

  // TITLE & FOOTER SETTINGS
  titleText: {
    description: "The main heading text displayed at the top of the simulation.",
  },
  titleFontFamily: {
    description: "The typeface used for the title text.",
  },
  titleFontWeight: {
    description: "The thickness or boldness of the title font.",
  },
  titleBold: {
    description: "Instantly force the title text to a heavy bold style.",
  },
  titleOffsetY: {
    description: "The vertical distance from the top of the screen to the title text.",
  },
  titleFontSize: {
    description: "The size of the title text in pixels.",
  },
  titleColor: {
    description: "The color of the title text.",
  },
  footerText: {
    description: "The subtext or credits displayed at the bottom of the simulation.",
  },
  footerFontFamily: {
    description: "The typeface used for the footer text.",
  },
  footerFontWeight: {
    description: "The thickness or boldness of the footer font.",
  },
  footerBold: {
    description: "Instantly force the footer text to a heavy bold style.",
  },
  footerOffsetY: {
    description: "The vertical distance from the bottom of the screen to the footer text.",
  },
  footerFontSize: {
    description: "The size of the footer text in pixels.",
  },
  footerTextColor: {
    description: "The color of the footer text.",
  },

  // AUDIO WORKSHOP & STORAGE
  melodyName: {
    description: "Assign a name to your custom melody before saving it to the cloud or your library.",
  },
  manualMelodyString: {
    description: "Paste raw frequency lists, JSON rhythm data, or MIDI base64 strings here to create custom songs.",
  },
  uploadMidi: {
    description: "Upload a standard MIDI file (.mid) to automatically convert it into a playable rhythmic sequence.",
  },
  storageTab: {
    description: "Switch between Local Storage (browser-based) and D1 Cloud Storage (persistent across devices).",
  },
  saveName: {
    description: "The name for your current configuration snapshot.",
  },
  exportAll: {
    description: "Download a full backup of all your local and cloud saves as a single JSON file.",
  },
  importAll: {
    description: "Restore your saves from a previously exported JSON backup file.",
  },
  jsonWorkshop: {
    description: "Directly view and edit the raw JSON data of your saves for advanced configuration management.",
  },

  // AUDIO STUDIO
  masterVolume: {
    description: "The overall volume level of the entire application.",
  },
  melodyVolume: {
    description: "The volume level of the background melodies and rhythmic sequences.",
  },
  fxVolume: {
    description: "The volume level of collision sounds and impact effects.",
  },
  audioCutoffEnabled: {
    description: "When enabled, starting a new sound will immediately stop the previous one to prevent audio overlapping.",
  },
  melodyInstrument: {
    description: "The virtual instrument used to play back rhythmic melodies.",
  },
  audioEcho: {
    description: "Enable or disable a stereo delay effect for the melody rack.",
  },
  audioDelayTime: {
    description: "The time interval between echo repetitions in seconds.",
  },
  audioDelayFeedback: {
    description: "Controls how many times the echo repeats. Higher values create longer trails.",
  },
  audioEchoWet: {
    description: "The balance between the dry original sound and the processed echo effect.",
  },
  audioLowPassFreq: {
    description: "The cutoff frequency for the low-pass filter. Lower values create a 'muffled' or 'underwater' sound.",
  },
  audioLowPassResonance: {
    description: "The resonance (Q) peak at the filter's cutoff frequency. Higher values add a 'ringing' quality.",
  },
  audioHighPassFreq: {
    description: "The cutoff frequency for the high-pass filter. Removes low-end frequencies for a 'thin' or 'radio' sound.",
  },
  audioCompThreshold: {
    description: "The decibel level at which the compressor starts reducing the volume of loud peaks.",
  },
  audioCompRatio: {
    description: "The amount of volume reduction applied once the signal exceeds the threshold.",
  },
  audioPitchShift: {
    description: "Shift the pitch of all audio up or down in semitones without changing playback speed.",
  },
  audioCompoundMode: {
    description: "An optimized audio engine designed to handle thousands of simultaneous collisions without performance lag.",
  },
  audioSlidingEnabled: {
    description: "Enable friction-based sounds when balls slide along walls or surfaces.",
  },
  audioSlidingVolume: {
    description: "The maximum volume of the sliding friction sound.",
  },
  audioSlidingPitch: {
    description: "The base pitch of the sliding friction sound.",
  },
  audioSlidingMaxSpeed: {
    description: "The speed at which the sliding sound reaches its maximum volume and pitch.",
  },
  songSliceDuration: {
    description: "The default duration for new slices added to the song timeline.",
  },
  songSliceContinuousMode: {
    description: "When enabled, slices will play sequentially in a loop rather than being triggered individually.",
  },
  audioMutedInSlicer: {
    description: "Mute the main simulation audio while using the song slicer workshop.",
  },
  songSpeedIncreaseEnabled: {
    description: "Gradually increase the playback speed of the song as more collisions occur.",
  },
  songSpeedMax: {
    description: "The maximum playback speed multiplier for the song speed-up effect.",
  },
  songResetEnabled: {
    description: "Automatically reset the song playback speed if no collisions occur for a set period.",
  },
  songResetTimer: {
    description: "The duration of silence required before the song playback speed resets to normal.",
  },
  soundEngineMode: {
    description: "Switch between configuring Collision Bounces (sounds when balls hit objects) and Ring Destructions (sounds when rings are broken).",
  },
  soundSourceType: {
    description: "Choose the source of the sound: Melody (global sequence), FX (pre-set sound effects), or Uploads (your custom audio files).",
  },
  selectedBallSoundEffect: {
    description: "Select a specific high-quality sound effect from the built-in library for ball collisions.",
  },
  userSoundLibrary: {
    description: "Manage your custom uploaded sounds. You can rename, preview, or delete them from the cloud.",
  },
  songConverter: {
    description: "Upload an MP3 file to automatically analyze its rhythm and convert it into a sequence of collision events.",
  },

  // BACKGROUND SETTINGS
  backgroundEnabled: {
    description: "Toggle the dynamic background visualization system.",
    modes: ["GLOBAL"]
  },
  backgroundEffect: {
    description: "The specific visual algorithm used to generate the background (e.g., Aurora, Plasma, Solid).",
    modes: ["GLOBAL"]
  },
  backgroundPrimaryColor: {
    description: "The main base color for the background effect.",
    modes: ["GLOBAL"]
  },
  backgroundSecondaryColor: {
    description: "The accent color used for gradients and multi-tone background effects.",
    modes: ["GLOBAL"]
  },
  backgroundTertiaryColor: {
    description: "A third color added to complex backgrounds for more depth and variation.",
    modes: ["GLOBAL"]
  },
  backgroundOpacity: {
    description: "How transparent the background effect is. Lower values reveal more of the base inner color.",
    modes: ["GLOBAL"]
  },

  // DRAWER & GENERATION
  drawerLineThickness: {
    description: "Thickness of the trail generated in Artisan mode.",
    modes: ["DRAWER"]
  },
  drawerLineOpacity: {
    description: "Transparency of the artisan line generation.",
    modes: ["DRAWER"]
  },
  drawerMinRadius: {
    description: "The starting radius for artisan expansion.",
    modes: ["DRAWER"]
  },
  drawerReverseMode: {
    description: "Inverts the expansion logic of the drawing engine.",
    modes: ["DRAWER"]
  },
  drawerOnlyOnBounce: {
    description: "When enabled, lines are only drawn from the point of impact when the ball bounces off a boundary.",
    modes: ["DRAWER"]
  },
  growthRate: {
    title: "Growth Rate",
    description: "The speed at which the artisan line expands.",
    modes: ["DRAWER"]
  },
  optimizationSettings: {
    title: "Optimization",
    description: "Deep-level performance and precision controls. AI-enhanced rendering and smart physics to ensure smooth performance on all hardware.",
  },
  useNumericGridKeys: {
    title: "Fast Spatial Hash",
    description: "Replaces string-based grid keys with packed integers. This eliminates millions of memory allocations per second, greatly reducing CPU stutter and GC pressure during high ball counts.",
  },
  enableFrustumCulling: {
    title: "Smart Render",
    description: "Automatically disable rendering for objects that are currently off-screen to save CPU/GPU resources.",
  },
  frustumCullingMargin: {
    title: "Safety Buffer",
    description: "The padding area around the screen before objects are culled. Higher values prevent objects from popping in at the edges.",
  },
  enableHyperSpeed: {
    title: "Turbo Mode",
    description: "Optimized playback engine for extreme speeds. Uses simplified math and visual batching to maintain responsiveness.",
  },
  hyperSpeedSimplifiedVisuals: {
    title: "Simplified FX",
    description: "Disables complex per-particle physics during high-speed bursts to maintain frame rate.",
  },
  hyperSpeedAutoTune: {
    title: "Auto-Tune",
    description: "Dynamically adjusts render quality based on current performance and object count.",
  },
  hyperSpeedBatchSize: {
    title: "Batch Density",
    description: "The number of objects processed in a single compute cycle. High values are faster but can cause micro-stutter.",
  },
  hyperSpeedRenderLimit: {
    title: "Draw Cap",
    description: "Maximum number of simultaneous objects rendered during Turbo Mode.",
  },
  hyperSpeedTargetTime: {
    title: "Step Precision",
    description: "Target frame timing for hyper-speed integration. Lower values result in smoother movement but higher CPU use.",
  },
  cullRingsOutsideScreen: {
    title: "Cull Rings",
    description: "Stop rendering rings that have shrunk past the field of view.",
  },
  cullBlastsOutsideScreen: {
    title: "Cull Blasts",
    description: "Stop rendering explosions and fragments that are no longer visible.",
  },
  partialRingBlastOptimization: {
    title: "Fragment Density",
    description: "Reduces the number of fragments generated in complex explosions to maintain high frame rates.",
  },

  // HUD & COUNTERS
  viewportOptics: {
    title: "Camera View",
    description: "Customize how the camera follows activity and responds to movement for a cinematic or stabilized viewing experience.",
  },
  parallaxSettings: {
    title: "Parallax Background",
    description: "Multilayered background animations that create a sense of depth as objects move within the simulation.",
  },
  parallaxWatermarkFont: {
    title: "Watermark Typography",
    description: "Select the font and weight for the repeating background text.",
  },
  parallaxWatermarkColor: {
    title: "Watermark Style",
    description: "Adjust color, opacity, and intensity for the background text patterns.",
  },
  parallaxWatermarkGeometry: {
    title: "Watermark Pattern",
    description: "Control the density, spacing, and rotation of the repeated text layers.",
  },
  parallaxWatermarkShadow: {
    title: "Watermark Shadow",
    description: "Add depth to background text using configurable drop shadows.",
  },
  ballPlacementSection: {
    title: "Ball Placement",
    description: "Define where and how spheres are spawned into the world at the start of a session.",
  },
  workshopSequenceName: {
    title: "Sequence Name",
    description: "Give your custom melody a unique name to identify it in the Cloud Archive and Melody Library.",
  },
  workshopDataStream: {
    title: "Note Data Stream",
    description: "Enter raw frequency data or MIDI base64 strings to manually construct a custom collision melody.",
  },
  midiHardwareImport: {
    title: "MIDI Hardware Import",
    description: "Upload a standard .mid or .midi file to extract its note and velocity data for rhythmic simulation.",
  },
  audioMaster: {
    title: "Volume & Mixer",
    description: "Master levels and routing for all simulation audio, including ambient music and collision sound effects.",
  },
  audioWorkshop: {
    title: "Synthesis & Melodies",
    description: "Experimental tools for creating, editing, and managing harmonic collision sequences and MIDI data.",
  },
  audioSamples: {
    title: "Sample Processing & Uploads",
    description: "Manage custom audio assets, record new samples, and convert existing songs into rhythmic triggers.",
  },
  ballSizeCounter: {
    title: "Ball Size HUD",
    description: "Configurable telemetry display for tracking the sphere's current dimensions in real-time.",
  },
  bounceCounter: {
    title: "Bounce Tracking",
    description: "Modular counter system that monitors and styles total impact events across the session.",
  },
  coreLogic: {
    title: "Core Logic",
    description: "The primary simulation architecture. Choose between spawning, drawing, escaping, or rhythmic modes.",
  },
  environmentCanvas: {
    title: "Environment Canvas",
    description: "Atmospheric and background visual settings. Control colors, dynamic effects, and the overall 'vibe' of the simulation arena.",
  },
  globalVisuals: {
    title: "Global Visuals",
    description: "Universal visual modifiers that affect shaking, decay rates, and color cycling across the entire application.",
  },
  spaceBoundary: {
    title: "Space Boundary",
    description: "Configure an external collision sphere or box that interacts with balls after they leave the central arena.",
  },
  midiBounceFiltering: {
    title: "MIDI Bounce Filter",
    description: "Advanced controls for filtering which MIDI notes trigger ball bounces based on pitch, velocity, and track ID.",
  },
  escapeSettings: {
    title: "Escape Mechanics",
    description: "Fine-tune the behavior of breaking rings, including speed, gap alignment, assist systems, and shrink rates.",
  },
  audioEffects: {
    title: "Audio Effects",
    description: "Apply real-time filters like reverb, delay, and distortion to the simulation's audio engines.",
  },
  slicerNode: {
    title: "Slicer Node",
    description: "Manage granular audio slicing. Trigger specific segments of a song based on collision events for precise synchronization.",
  },
  sampleTriggerNode: {
    title: "Sample Trigger Node",
    description: "Assign specific sound effects or custom uploads to bounce and break events. Manage your local the cloud-stored audio archives.",
  },
  neuralAnalysisHub: {
    title: "Neural Analysis Hub",
    description: "Advanced signal processing unit that converts standard audio files into responsive simulation triggers via frequency analysis.",
  },
  isCloudRendering: {
    title: "Cloud Engine",
    description: "Expert Only: Offload video encoding to external servers for perfect 4K exports regardless of your local machine speed.",
    keywords: ["cloud", "encoding", "render", "export", "offload"]
  },
};
